/**
 * Keyboard support for a SearchableSelect's open list: the arrows move the
 * choice through the filtered options (scrolling it into view), Enter picks
 * the current one, Escape closes.
 */

import type { KeyboardEvent, RefObject } from 'react';

import type { SearchableSelectOption } from './SearchableSelect';

interface SelectKeysOptions<T> {
  open: boolean;
  options: SearchableSelectOption<T>[];
  value: T;
  listRef: RefObject<HTMLDivElement>;
  onChange: (value: T) => void;
  onPick: (option: SearchableSelectOption<T>) => void;
  onClose: () => void;
}

const STEP: Partial<Record<string, number>> = { ArrowDown: 1, ArrowUp: -1 };

export function searchableSelectKeys<T>({ open, options, value, listRef, onChange, onPick, onClose }: SelectKeysOptions<T>) {
  const moveTo = (index: number) => {
    const option = options[index];
    if (!option) return;
    onChange(option.value);
    listRef.current?.children[index]?.scrollIntoView({ block: 'nearest' });
  };
  const pick = (e: KeyboardEvent<HTMLDivElement>, index: number) => {
    if (!options[index]) return;
    e.preventDefault();
    onPick(options[index]);
  };
  return (e: KeyboardEvent<HTMLDivElement>) => {
    if (!open) return;
    if (e.key === 'Escape') return onClose();
    const current = options.findIndex((o) => o.value === value);
    const step = STEP[e.key];
    if (step) {
      e.preventDefault();
      return moveTo(Math.min(Math.max(current + step, 0), options.length - 1));
    }
    if (e.key === 'Enter') pick(e, current);
  };
}
